<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { validateEmail } from "./validations";
  import { _token } from "@ui/currentUser";

  export let email: string = "";
  export let compromised: boolean = false;
  /**
   * The shared red banner above this form. Bound two-way so that editing the address can take the
   * message back down: every message this screen can show is ABOUT the address in the box
   * ("Hmm... sorry we can't seem to find this email", or the SchoolStatus refusal), so the moment
   * the address changes the message is describing something the user is no longer asking for.
   *
   * Say a teacher types `sara@exapmle.com`, is told we cannot find it, and corrects the typo to
   * `sara@example.com`. Without this she is still being told her address does not exist while
   * looking at an address that does.
   */
  export let errorCode: string | undefined = undefined;

  const dispatcher = createEventDispatcher();

  let emailInput: HTMLInputElement;

  /** Only user edits, never the initial value the server puts in the box. */
  function addressEdited() {
    errorCode = undefined;
  }

  $: [emailValid, emailValidationMessage] = validateEmail(email, "login");
  $: canSubmitResetPasswordForm = emailValid;

  function rememberdPassword() {
    dispatcher("modeChanged", "login");
  }

  onMount(() => {
    if (emailInput) {
      emailInput.focus();
    }
  });
</script>

<h2 class="mb-4 text-18 font-bold text-nxgray-600">Reset your Smore password</h2>
<form class="flex w-full flex-col items-start" method="POST" action="/app/auth/send_reset_password_email">
  <label class="mb-1 text-13 text-secondary" for="email">Email address</label>
  <input type="hidden" name="new_flow" value="1" />
  <input type="hidden" name="compromised" value={compromised} />
  <input type="hidden" name="_token" value={$_token} required />
  <input
    class="focusable w-full text-14"
    class:valid={emailValid}
    type="text"
    name="email"
    autocomplete="email"
    bind:this={emailInput}
    bind:value={email}
    on:input={addressEdited}
    placeholder="name@email.com" />
  <div class="text-13 text-danger-600" class:visible={emailValidationMessage} class:invisible={!emailValidationMessage}>
    {emailValidationMessage || ""}
  </div>
  <input
    type="submit"
    disabled={!canSubmitResetPasswordForm}
    class:opacity-30={!canSubmitResetPasswordForm}
    class="focusable pointer-events-none mt-4 w-full select-none rounded bg-blue-700 py-3 text-center text-15 font-bold text-white hover:bg-blue-800"
    class:pointer-events-auto={canSubmitResetPasswordForm}
    class:clickable={canSubmitResetPasswordForm}
    value="Reset Password" />
</form>
<button
  class="clickable focusable mt-5 flex w-full justify-center text-13 font-medium text-teal-700 underline outline-none hover:text-teal-800"
  on:click={rememberdPassword}>I remembered my password</button>

<style>
  input:not([type="submit"]) {
    padding-left: theme("padding.3");
    padding-right: theme("padding.3");
    padding-top: theme("padding.2");
    padding-bottom: theme("padding.2");
    border-radius: theme("borderRadius.DEFAULT");
    border-width: theme("borderWidth.DEFAULT");
    color: theme("textColor.nxgray.600");
    outline-offset: theme("outlineOffset.2");
    outline-width: theme("outlineWidth.2");
    outline-color: theme("outlineColor.transparent");
    outline-style: solid;
  }

  input:not([type="submit"]):not(.valid):not(:placeholder-shown):focus,
  input:not([type="submit"]):not(.valid):not(:placeholder-shown) {
    border-color: theme("borderColor.red.500");
  }

  input:not([type="submit"]).valid:focus,
  input:not([type="submit"]).valid {
    border-color: theme("borderColor.green.500");
  }
</style>
